import { Box, Typography } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { memo, useEffect, useState } from "react";
import { CarSelectOptionField } from "@app/components/Inputs";
import {
  CarInfoCell,
  CarInfoHeader,
  CarInfoTable,
} from "@app/components/InfoTable";
import { CarButton, CarLoadingPromiseButton } from "@app/components/Buttons";
import { api } from "@app/api/carApi.manual";
import { useSimulationGroups } from "@app/pages/assetAllocation/useSimulationGroups";
import { useDialog } from "@app/hooks/useDialog";
import { SimulationSettingsDialog } from "./SimulationSettingsDialog";
import { getTimeoutPromise } from "@app/utils";
import { InProgressDialog } from "@app/components/InProgressDialog";
import { CarPageContent } from "@app/components/PageContent";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarModuleSectionCode, useUserAccess } from "@app/hooks/useUserAccess";
import { useShowMessage } from "@app/hooks/useShowMessage";
import { CarStatusFail, CarStatusSuccess } from "@app/components/Icons";
import { useStore } from "react-redux";
import { getFetch } from "@app/api/carBaseQuery";
import { SimulationSimulationJobRetrieveApiResponse } from "@app/api/carApi.generated";

export const SimulationRun = memo(() => {
  const [loadSimulationList] = api.useLazySimulationSimulationJobListQuery();
  const store = useStore();

  const dialog = useDialog();
  const [simulationGroupId, setSimulationGroupId] = useState<
    string | undefined
  >();
  const simulationGroups = useSimulationGroups();
  const userAccess = useUserAccess();
  const showMessage = useShowMessage();

  useEffect(() => {
    if (!simulationGroupId && simulationGroups.defOptionValue) {
      setSimulationGroupId(simulationGroups.defOptionValue);
    }
  }, [simulationGroups.defOptionValue, simulationGroupId]);

  const [runRequest] = api.useSimulationSimulationJobAdminCreateMutation();

  const checkJob = getFetch<
    SimulationSimulationJobRetrieveApiResponse,
    undefined
  >(store);

  const waitTillJobIsRunning = async (simulationJobId: string) => {
    let isRunning = true;
    while (isRunning) {
      await getTimeoutPromise(1000);

      const responseData = await checkJob({
        url: `/api/simulation/simulation_job/${simulationJobId}/`,
      });

      if (responseData) {
        const status = responseData?.status;
        if (status === "C" || status === "E") {
          isRunning = false;
          showMessage({
            label: "SimulationRun",
            message:
              status === "C" ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CarStatusSuccess />
                  <Typography variant="par01Regular" sx={{ mt: 0.25 }}>
                    Simulation run completed successfully.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CarStatusFail />
                  <Typography variant="par01Regular" sx={{ mt: 0.25 }}>
                    Simulation run completed with errors.
                  </Typography>
                </Box>
              ),
          });
        }
      } else {
        isRunning = false;
      }
    }
  };

  const handleLaunch = async () => {
    if (!simulationGroupId) {
      return;
    }

    const lastSim = (await loadSimulationList().unwrap())
      ?.filter((i) => i.group === simulationGroupId)
      .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))
      .at(0);

    const lastRunTime = lastSim
      ? new Date(lastSim.updated_at ?? "").valueOf() -
        new Date(lastSim.created_at ?? "").valueOf()
      : undefined;

    const runResponse = await runRequest({
      simulationJobCreate: {
        simulation_group_id: simulationGroupId,
      },
    });

    if (!runResponse.data) {
      return;
    }
    const simulationJobId = runResponse.data.id;

    if (!simulationJobId) {
      return;
    }

    dialog(InProgressDialog, {
      message: `The simulation is running.\nPlease wait.`,
      lastRunTime,
      run: () => waitTillJobIsRunning(simulationJobId),
    });
  };

  const handleRunSettings = () => {
    if (simulationGroupId) {
      dialog(SimulationSettingsDialog, { simulationGroupId });
    }
  };

  const pageContent = usePageContent();

  return (
    <TablePlaceHolder
      sx={{ mx: 7, mt: 10 }}
      isLoading={simulationGroups.isLoading || pageContent.isLoading}
      rows={8}
    >
      <Typography variant="h2SSemiBold" sx={{ mt: 4, mb: 5 }}>
        Run
      </Typography>

      <CarPageContent
        sx={{ mb: 5 }}
        content={pageContent.getContent(
          CarPageContentCode.SIMULATION_ANALYSIS_RUN,
        )}
      />

      <CarInfoTable sx={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
        <CarInfoHeader text="Simulation Group" />
        <CarInfoHeader text="Settings" sx={{ py: 4 }} />
        <CarInfoHeader text="Launch" />
        <CarInfoCell isOdd>
          <CarSelectOptionField
            sx={{ width: 240 }}
            options={simulationGroups.options}
            value={simulationGroupId}
            onChange={setSimulationGroupId}
          />
        </CarInfoCell>
        <CarInfoCell isOdd sx={{ py: 2.5 }}>
          <CarButton
            variant="outlined"
            onClick={handleRunSettings}
            disabled={
              !userAccess.hasModuleSectionAccess(
                CarModuleSectionCode.SIMULATION_ANALYSIS_SIMULATION_RUN_SETTINGS,
              )
            }
          >
            Run Settings
          </CarButton>
        </CarInfoCell>
        <CarInfoCell isOdd>
          <CarLoadingPromiseButton variant="contained" onClick={handleLaunch}>
            Launch
          </CarLoadingPromiseButton>
        </CarInfoCell>
      </CarInfoTable>
    </TablePlaceHolder>
  );
});
